import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Check, ChevronLeft, ChevronRight, Clock, Loader2, MapPin, Minus, Pencil, Plus, Search, ShoppingCart, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fetchAdminCustomers } from "@/lib/adminCustomersApi";
import { fetchCatalogSnapshotForCustomer, type CatalogSnapshot } from "@/lib/catalogApi";
import { fetchDeliveryUnits, type DeliveryUnit } from "@/lib/adminDeliveryUnitsApi";
import { createManualOrder, type ManualOrderItemPayload, type OrderChannel } from "@/lib/ordersApi";
import { CLIENT_TYPE_LABELS } from "@/lib/clientTypeLabels";
import type { AdminClientSummary, CatalogCategory, Product } from "@/lib/types";

const CHANNEL_OPTIONS: { value: OrderChannel; label: string; icon: string }[] = [
  { value: "WHATSAPP", label: "WhatsApp", icon: "💬" },
  { value: "TELEPHONE", label: "Telefone", icon: "☎️" },
  { value: "IN_PERSON", label: "Presencial", icon: "👤" },
  { value: "OTHER", label: "Outro", icon: "✉️" },
];

const DELIVERY_WINDOWS = ["08h – 12h", "12h – 16h", "16h – 20h"];
// Sentinel — quando selecionado, o operador digita a janela em um input livre.
// O valor real persistido vem desse input, não da string "__custom__".
const CUSTOM_WINDOW = "__custom__";

type Step = "context" | "products";

type CartItem = ManualOrderItemPayload & {
  product: Product;
};

export default function AdminNewOrder() {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("context");

  const [clients, setClients] = useState<AdminClientSummary[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [catalog, setCatalog] = useState<CatalogSnapshot | null>(null);
  // false = sem cliente ainda; true enquanto busca o catálogo dele.
  const [catalogLoading, setCatalogLoading] = useState(false);

  const [customerId, setCustomerId] = useState<string>("");

  const [deliveryUnits, setDeliveryUnits] = useState<DeliveryUnit[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [deliveryUnitId, setDeliveryUnitId] = useState<string>("");

  const [channel, setChannel] = useState<OrderChannel>("WHATSAPP");
  const [channelDetail, setChannelDetail] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliveryWindow, setDeliveryWindow] = useState(DELIVERY_WINDOWS[0]);
  const [notes, setNotes] = useState("");

  const [activeCategory, setActiveCategory] = useState<string>("ALL");
  const [productQuery, setProductQuery] = useState("");
  const [cart, setCart] = useState<CartItem[]>([]);
  const [addTarget, setAddTarget] = useState<Product | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const list = await fetchAdminCustomers();
        setClients(list.filter((c) => c.status === "VALIDADO"));
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Falha ao carregar clientes");
      } finally {
        setClientsLoading(false);
      }
    })();
  }, []);

  // Unidades de entrega são carregadas por cliente. Trocar de cliente esvazia o carrinho e zera a
  // unidade selecionada (produtos/preços podem ser diferentes).
  useEffect(() => {
    if (!customerId) {
      setCatalog(null);
      setDeliveryUnits([]);
      setDeliveryUnitId("");
      setCart([]);
      return;
    }
    let cancelled = false;
    setUnitsLoading(true);
    setCart([]);
    setDeliveryUnitId("");
    (async () => {
      try {
        const units = await fetchDeliveryUnits(customerId);
        if (!cancelled) setDeliveryUnits(units.filter((u) => u.active));
      } catch (error) {
        if (!cancelled) toast.error(error instanceof Error ? error.message : "Falha ao carregar unidades do cliente");
      } finally {
        if (!cancelled) setUnitsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  // O catálogo (e os PREÇOS) dependem da unidade de entrega: quando a unidade tem tabela própria
  // (override), os preços saem dela — a mesma regra do checkout/pedido. Por isso recarregamos ao trocar
  // a unidade, re-precificando os itens já no carrinho (itens sem preço na nova tabela são removidos).
  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    setCatalogLoading(true);
    (async () => {
      try {
        const snapshot = await fetchCatalogSnapshotForCustomer(customerId, deliveryUnitId || null);
        if (cancelled) return;
        setCatalog(snapshot);
        setCart((prev) => {
          if (prev.length === 0) return prev;
          let dropped = 0;
          const next = prev.flatMap((it) => {
            const fresh = snapshot.products.find((p) => p.id === it.product.id);
            if (!fresh || priceFor(fresh, it.unit) == null) {
              dropped += 1;
              return [];
            }
            return [{ ...it, product: fresh }];
          });
          if (dropped > 0) {
            toast.warning(`${dropped} item(ns) sem preço nesta unidade foram removidos do carrinho.`);
          }
          return next;
        });
      } catch (error) {
        if (!cancelled) toast.error(error instanceof Error ? error.message : "Falha ao carregar catálogo do cliente");
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, deliveryUnitId]);

  const categories: CatalogCategory[] = useMemo(() => {
    const all = catalog?.categories ?? [];
    return all.filter((c) => c.active && !c.operatorOnly).sort((a, b) => a.sortOrder - b.sortOrder);
  }, [catalog]);

  const productsForView = useMemo(() => {
    const products = (catalog?.products ?? []).filter((p) => p.active);
    const byCat = activeCategory === "ALL" ? products : products.filter((p) => p.category === activeCategory);
    const q = productQuery.trim().toLowerCase();
    const filtered = q ? byCat.filter((p) => p.name.toLowerCase().includes(q)) : byCat;
    return filtered.slice(0, 60);
  }, [catalog, activeCategory, productQuery]);

  const selectedClient = clients.find((c) => c.id === customerId) ?? null;
  const selectedUnit = deliveryUnits.find((u) => u.id === deliveryUnitId) ?? null;
  // Cliente titular é âncora não-operacional: o pedido manual em nome dele EXIGE escolher a unidade.
  const titularNeedsUnit = selectedClient?.accountKind === "TITULAR" && !deliveryUnitId;
  const contextValid =
    !!customerId &&
    !!channel &&
    (channel !== "OTHER" || channelDetail.trim().length > 0) &&
    !!deliveryDate &&
    !!deliveryWindow &&
    !titularNeedsUnit;

  const total = useMemo(
    () => cart.reduce((sum, it) => sum + (priceFor(it.product, it.unit) ?? 0) * it.quantity, 0),
    [cart],
  );

  const composedDeliveryWindow = deliveryDate
    ? `${deliveryDate.split("-").reverse().join("/")} · ${deliveryWindow}`
    : "";

  const goToProducts = () => {
    if (!contextValid) {
      toast.error("Preencha cliente, canal, data e janela de entrega antes de continuar.");
      return;
    }
    setStep("products");
  };

  const addToCart = (product: Product, unit: string, quantity: number, cut: string | null, observation?: string) => {
    if (!Number.isFinite(quantity) || quantity <= 0) {
      toast.error("Quantidade inválida");
      return;
    }
    const obs = observation?.trim() || undefined;
    setCart((prev) => {
      // Linhas no carrinho são únicas por (produto, unidade, corte) — quantidade soma para a
      // mesma combinação; cortes diferentes ficam em linhas separadas.
      const existingIdx = prev.findIndex(
        (it) => it.productId === product.id && it.unit === unit && (it.cut ?? null) === (cut ?? null),
      );
      if (existingIdx >= 0) {
        const next = [...prev];
        next[existingIdx] = {
          ...next[existingIdx],
          quantity: next[existingIdx].quantity + quantity,
          // Mantém a observação existente; só preenche se ainda não houver uma.
          observation: next[existingIdx].observation ?? obs,
        };
        return next;
      }
      return [
        ...prev,
        {
          productId: product.id,
          unit,
          quantity,
          cut,
          observation: obs,
          product,
        },
      ];
    });
  };

  const updateCartItem = (idx: number, patch: Partial<CartItem>) => {
    setCart((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const removeCartItem = (idx: number) => {
    setCart((prev) => prev.filter((_, i) => i !== idx));
  };

  const canSubmit = contextValid && cart.length > 0 && cart.every((it) => it.quantity > 0) && !submitting;

  const onSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const order = await createManualOrder({
        customerId,
        channel,
        channelDetail: channel === "OTHER" ? channelDetail.trim() : null,
        deliveryWindow: composedDeliveryWindow || null,
        deliveryUnitId: deliveryUnitId || null,
        notes: notes.trim() || null,
        items: cart.map(({ productId, unit, quantity, cut, observation }) => ({
          productId,
          unit,
          quantity,
          cut,
          observation: observation?.trim() || null,
        })),
      });
      toast.success(`Pedido criado: ${order.number ? `NX-${order.number}` : order.id}`);
      navigate("/admin/operacoes");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Falha ao criar pedido");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">Novo pedido manual</h1>
        <p className="text-sm text-muted-foreground">
          Crie um pedido em nome de um cliente já existente quando o contato vier por outro canal.
        </p>
      </header>

      <Stepper step={step} onJump={(s) => { if (s === "context" || contextValid) setStep(s); }} />

      {step === "context" ? (
        <ContextStep
          clients={clients}
          clientsLoading={clientsLoading}
          customerId={customerId}
          onSelectCustomer={setCustomerId}
          deliveryUnits={deliveryUnits}
          unitsLoading={unitsLoading}
          deliveryUnitId={deliveryUnitId}
          onSelectDeliveryUnit={setDeliveryUnitId}
          titularNeedsUnit={titularNeedsUnit}
          channel={channel}
          onChannelChange={setChannel}
          channelDetail={channelDetail}
          onChannelDetailChange={setChannelDetail}
          deliveryDate={deliveryDate}
          onDeliveryDateChange={setDeliveryDate}
          deliveryWindow={deliveryWindow}
          onDeliveryWindowChange={setDeliveryWindow}
          notes={notes}
          onNotesChange={setNotes}
          canContinue={contextValid}
          onCancel={() => navigate(-1)}
          onContinue={goToProducts}
        />
      ) : (
        <ProductsStep
          summary={{
            client: selectedClient,
            channelLabel: CHANNEL_OPTIONS.find((c) => c.value === channel)?.label ?? channel,
            channelDetail: channel === "OTHER" ? channelDetail : null,
            deliveryWindow: composedDeliveryWindow,
            deliveryUnitLabel: selectedUnit ? `${selectedUnit.externalCode} · ${selectedUnit.name}` : null,
            notes,
          }}
          onEditContext={() => setStep("context")}
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
          productQuery={productQuery}
          onProductQueryChange={setProductQuery}
          productsForView={productsForView}
          catalogLoading={catalogLoading}
          onAddProductClick={(p) => setAddTarget(p)}
          cart={cart}
          onUpdateItem={updateCartItem}
          onRemoveItem={removeCartItem}
          total={total}
          canSubmit={canSubmit}
          submitting={submitting}
          onSubmit={onSubmit}
        />
      )}

      <AddItemDialog
        product={addTarget}
        onClose={() => setAddTarget(null)}
        onConfirm={(unit, qty, cut, observation) => {
          if (addTarget) addToCart(addTarget, unit, qty, cut, observation);
          setAddTarget(null);
        }}
      />
    </div>
  );
}

function Stepper({ step, onJump }: { step: Step; onJump: (s: Step) => void }) {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => onJump("context")}
        className={`flex items-center gap-2 text-sm ${step === "context" ? "font-semibold" : "text-muted-foreground"}`}
      >
        <span
          className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
            step === "context" ? "bg-primary text-primary-foreground" : "bg-primary/15 text-primary"
          }`}
        >
          {step === "context" ? "1" : <Check className="h-3.5 w-3.5" />}
        </span>
        Contexto
      </button>
      <span className="h-px w-12 bg-border" />
      <button
        type="button"
        onClick={() => onJump("products")}
        className={`flex items-center gap-2 text-sm ${step === "products" ? "font-semibold" : "text-muted-foreground"}`}
      >
        <span
          className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
            step === "products" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
          }`}
        >
          2
        </span>
        Produtos
      </button>
    </div>
  );
}

function ContextStep(props: {
  clients: AdminClientSummary[];
  clientsLoading: boolean;
  customerId: string;
  onSelectCustomer: (id: string) => void;
  deliveryUnits: DeliveryUnit[];
  unitsLoading: boolean;
  deliveryUnitId: string;
  onSelectDeliveryUnit: (id: string) => void;
  titularNeedsUnit: boolean;
  channel: OrderChannel;
  onChannelChange: (v: OrderChannel) => void;
  channelDetail: string;
  onChannelDetailChange: (v: string) => void;
  deliveryDate: string;
  onDeliveryDateChange: (v: string) => void;
  deliveryWindow: string;
  onDeliveryWindowChange: (v: string) => void;
  notes: string;
  onNotesChange: (v: string) => void;
  canContinue: boolean;
  onCancel: () => void;
  onContinue: () => void;
}) {
  return (
    <>
      <div className="grid gap-5 md:grid-cols-3">
        <section className="md:col-span-2 space-y-3 rounded-xl border bg-card p-5">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">1</span>
            Cliente
          </h2>
          <ClientCombobox
            clients={props.clients}
            loading={props.clientsLoading}
            value={props.customerId}
            onChange={props.onSelectCustomer}
          />

          {props.customerId ? (
            <div className="border-t pt-3">
              <Label className="mb-1.5 flex items-center gap-1.5">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                Unidade de entrega
              </Label>
              <DeliveryUnitCombobox
                units={props.deliveryUnits}
                loading={props.unitsLoading}
                value={props.deliveryUnitId}
                onChange={props.onSelectDeliveryUnit}
              />
              {props.titularNeedsUnit && (
                <p className="mt-1.5 text-xs text-destructive">
                  Cliente titular: selecione a unidade de entrega para continuar.
                </p>
              )}
            </div>
          ) : null}
        </section>

        <section className="space-y-3 rounded-xl border bg-card p-5">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">2</span>
            Canal
          </h2>
          <div className="space-y-2">
            {CHANNEL_OPTIONS.map((opt) => {
              const selected = props.channel === opt.value;
              return (
                <label
                  key={opt.value}
                  className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 p-2.5 text-sm transition ${
                    selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                  }`}
                >
                  <input
                    type="radio"
                    name="channel"
                    className="accent-primary"
                    checked={selected}
                    onChange={() => props.onChannelChange(opt.value)}
                  />
                  <span className="text-lg">{opt.icon}</span>
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
          {props.channel === "OTHER" ? (
            <div className="space-y-1">
              <Label htmlFor="channelDetail">Descreva o canal</Label>
              <Input
                id="channelDetail"
                value={props.channelDetail}
                onChange={(e) => props.onChannelDetailChange(e.target.value)}
                placeholder="Ex: Instagram, e-mail..."
                data-testid="channel-detail"
              />
            </div>
          ) : null}
        </section>

        <section className="md:col-span-2 space-y-3 rounded-xl border bg-card p-5">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">3</span>
            Entrega
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="deliveryDate">Data</Label>
              <DatePicker id="deliveryDate" value={props.deliveryDate} onChange={props.onDeliveryDateChange} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="deliveryWindow">Janela</Label>
              {(() => {
                // Custom mode = valor digitado não bate com nenhum preset.
                // Mantemos o select como fonte de modo: trocar para um preset
                // sobrescreve o que estava digitado; trocar para "Personalizado..."
                // limpa o valor para o operador digitar do zero.
                const isCustom = !DELIVERY_WINDOWS.includes(props.deliveryWindow);
                return (
                  <>
                    <select
                      id="deliveryWindow"
                      value={isCustom ? CUSTOM_WINDOW : props.deliveryWindow}
                      onChange={(e) => {
                        const next = e.target.value;
                        props.onDeliveryWindowChange(next === CUSTOM_WINDOW ? "" : next);
                      }}
                      className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                    >
                      {DELIVERY_WINDOWS.map((w) => (
                        <option key={w} value={w}>{w}</option>
                      ))}
                      <option value={CUSTOM_WINDOW}>Personalizado…</option>
                    </select>
                    {isCustom && (
                      <Input
                        className="mt-2"
                        placeholder="ex.: 08h às 10h ou das 13h às 15h"
                        value={props.deliveryWindow}
                        onChange={(e) => props.onDeliveryWindowChange(e.target.value)}
                        autoFocus
                      />
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </section>

        <section className="space-y-3 rounded-xl border bg-card p-5">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">4</span>
            Observações
          </h2>
          <Textarea
            value={props.notes}
            onChange={(e) => props.onNotesChange(e.target.value)}
            rows={6}
            placeholder="Ex: deixar com o porteiro, ligar antes..."
          />
        </section>
      </div>

      <div className="mt-5 flex items-center justify-between">
        <p className="text-xs text-muted-foreground">Cliente, canal, data e janela de entrega são obrigatórios.</p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={props.onCancel}>
            Cancelar
          </Button>
          <Button type="button" onClick={props.onContinue} disabled={!props.canContinue}>
            Continuar para produtos <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>
    </>
  );
}

function clientLabel(c: AdminClientSummary): string {
  return c.tradeName || c.companyName || c.email;
}

function clientSubtitle(c: AdminClientSummary): string {
  return c.email + (c.clientType ? ` · ${CLIENT_TYPE_LABELS[c.clientType]}` : "");
}

function ClientCombobox({
  clients,
  loading,
  value,
  onChange,
}: {
  clients: AdminClientSummary[];
  loading: boolean;
  value: string;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = clients.find((c) => c.id === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(
      (c) =>
        (c.tradeName ?? "").toLowerCase().includes(q) ||
        (c.companyName ?? "").toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q),
    );
  }, [clients, query]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando clientes...
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
        Nenhum cliente validado encontrado.
      </p>
    );
  }

  // Cliente escolhido e busca fechada: resumo colapsado, com opção de trocar.
  if (selected && !open) {
    return (
      <div className="flex items-center gap-3 rounded-lg border-2 border-primary bg-primary/5 p-3">
        <span className="text-2xl">🏪</span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{clientLabel(selected)}</div>
          <div className="truncate text-xs text-muted-foreground">{clientSubtitle(selected)}</div>
        </div>
        <button
          type="button"
          onClick={() => { setOpen(true); setQuery(""); }}
          className="shrink-0 text-xs font-medium text-primary hover:underline"
        >
          Trocar
        </button>
        <Check className="h-5 w-5 shrink-0 text-primary" />
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus={open}
          placeholder="Buscar por nome, CNPJ ou email..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          className="pl-9"
          data-testid="client-search"
        />
      </div>
      {open ? (
        <div
          className="absolute z-20 mt-1 max-h-72 w-full space-y-1 overflow-auto rounded-lg border bg-popover p-1 shadow-md"
          role="listbox"
          aria-label="Clientes"
        >
          {filtered.length === 0 ? (
            <p className="p-3 text-center text-sm text-muted-foreground">Nenhum cliente encontrado.</p>
          ) : (
            filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                role="option"
                aria-selected={c.id === value}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange(c.id); setOpen(false); setQuery(""); }}
                className="flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-primary/10"
              >
                <span className="text-2xl">🏪</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{clientLabel(c)}</span>
                  <span className="block truncate text-xs text-muted-foreground">{clientSubtitle(c)}</span>
                </span>
                {c.id === value ? <Check className="h-5 w-5 shrink-0 text-primary" /> : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function unitAddress(u: DeliveryUnit): string {
  return [u.address, u.district, u.city].filter(Boolean).join(" · ");
}

function DeliveryUnitCombobox({
  units,
  loading,
  value,
  onChange,
}: {
  units: DeliveryUnit[];
  loading: boolean;
  value: string;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = units.find((u) => u.id === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return units;
    return units.filter(
      (u) => u.name.toLowerCase().includes(q) || u.externalCode.toLowerCase().includes(q),
    );
  }, [units, query]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando unidades...
      </div>
    );
  }

  if (units.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
        <span className="flex-1">Este cliente ainda não tem unidades cadastradas.</span>
        <Link to="/admin/unidades-entrega" className="whitespace-nowrap font-medium text-primary hover:underline">
          Gerenciar unidades
        </Link>
      </div>
    );
  }

  // Unidade escolhida e busca fechada: mostra o resumo da unidade (endereço/janela).
  if (selected && !open) {
    const address = unitAddress(selected);
    return (
      <div className="flex items-start gap-2 rounded-lg border-2 border-primary bg-primary/5 p-3">
        <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 font-mono text-xs text-primary">
          {selected.externalCode}
        </span>
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="truncate text-sm font-semibold">{selected.name}</div>
          {address ? (
            <div className="flex items-center gap-1 truncate text-xs text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0" /> {address}
            </div>
          ) : null}
          {selected.deliveryWindowText ? (
            <div className="flex items-center gap-1 truncate text-xs text-muted-foreground">
              <Clock className="h-3 w-3 shrink-0" /> {selected.deliveryWindowText}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => { setOpen(true); setQuery(""); }}
          className="shrink-0 text-xs font-medium text-primary hover:underline"
        >
          Trocar
        </button>
        <button
          type="button"
          onClick={() => onChange("")}
          className="shrink-0 text-muted-foreground hover:text-destructive"
          aria-label="Remover unidade"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus={open}
          placeholder="Buscar unidade por código ou nome..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          // Atraso curto para o clique numa opção registrar antes do blur fechar a lista.
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          className="pl-9"
          data-testid="delivery-unit-search"
        />
      </div>
      {open ? (
        <div className="absolute z-20 mt-1 max-h-60 w-full space-y-1 overflow-auto rounded-lg border bg-popover p-1 shadow-md">
          {filtered.length === 0 ? (
            <p className="p-3 text-center text-sm text-muted-foreground">Nenhuma unidade encontrada.</p>
          ) : (
            filtered.map((u) => {
              const address = unitAddress(u);
              return (
                <button
                  key={u.id}
                  type="button"
                  // mousedown preventDefault evita o blur do input antes do onClick.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { onChange(u.id); setOpen(false); setQuery(""); }}
                  className="flex w-full items-start gap-2 rounded-md p-2 text-left hover:bg-primary/10"
                >
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground">
                    {u.externalCode}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{u.name}</span>
                    {address ? <span className="block truncate text-xs text-muted-foreground">{address}</span> : null}
                  </span>
                  {u.id === value ? <Check className="h-4 w-4 shrink-0 text-primary" /> : null}
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}

function ProductsStep(props: {
  summary: {
    client: AdminClientSummary | null;
    channelLabel: string;
    channelDetail: string | null;
    deliveryWindow: string;
    deliveryUnitLabel: string | null;
    notes: string;
  };
  onEditContext: () => void;
  categories: CatalogCategory[];
  activeCategory: string;
  onCategoryChange: (c: string) => void;
  productQuery: string;
  onProductQueryChange: (v: string) => void;
  productsForView: Product[];
  catalogLoading: boolean;
  onAddProductClick: (p: Product) => void;
  cart: CartItem[];
  onUpdateItem: (idx: number, patch: Partial<CartItem>) => void;
  onRemoveItem: (idx: number) => void;
  total: number;
  canSubmit: boolean;
  submitting: boolean;
  onSubmit: () => void;
}) {
  const { summary } = props;
  return (
    <>
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-sm">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-semibold">🏪 {summary.client?.tradeName || summary.client?.companyName || summary.client?.email || "—"}</span>
          <span className="text-muted-foreground">·</span>
          <span>📞 {summary.channelLabel}{summary.channelDetail ? ` (${summary.channelDetail})` : ""}</span>
          {summary.deliveryUnitLabel ? (
            <>
              <span className="text-muted-foreground">·</span>
              <span>🏭 {summary.deliveryUnitLabel}</span>
            </>
          ) : null}
          {summary.deliveryWindow ? (
            <>
              <span className="text-muted-foreground">·</span>
              <span>🚚 {summary.deliveryWindow}</span>
            </>
          ) : null}
          {summary.notes ? (
            <>
              <span className="text-muted-foreground">·</span>
              <span className="italic text-muted-foreground">"{summary.notes}"</span>
            </>
          ) : null}
          <button
            type="button"
            className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            onClick={props.onEditContext}
          >
            <Pencil className="h-3 w-3" /> Editar contexto
          </button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <div className="space-y-3 rounded-xl border bg-card overflow-hidden">
          <div className="flex items-center gap-2 overflow-x-auto border-b px-3 py-2">
            <CategoryPill
              active={props.activeCategory === "ALL"}
              onClick={() => props.onCategoryChange("ALL")}
              label="Todos"
            />
            {props.categories.map((c) => (
              <CategoryPill
                key={c.code}
                active={props.activeCategory === c.code}
                onClick={() => props.onCategoryChange(c.code)}
                label={`${c.emoji} ${c.label}`}
              />
            ))}
          </div>

          <div className="px-5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar produto..."
                value={props.productQuery}
                onChange={(e) => props.onProductQueryChange(e.target.value)}
                className="pl-9"
                data-testid="product-search"
              />
            </div>
          </div>

          <div className="px-5 pb-5 pt-2">
            {props.catalogLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Carregando catálogo...
              </div>
            ) : props.productsForView.length === 0 ? (
              <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                Nenhum produto encontrado.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {props.productsForView.map((p) => {
                  const displayUnit = displayUnitOf(p);
                  const price = priceFor(p, displayUnit);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => props.onAddProductClick(p)}
                      className="group flex flex-col items-stretch rounded-lg border p-3 text-left transition hover:border-primary hover:shadow-sm"
                    >
                      <div className="text-3xl">{p.emoji ?? "📦"}</div>
                      <div className="mt-1 line-clamp-2 text-sm font-medium">{p.name}</div>
                      <div className="mt-1.5 flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          {price != null ? `${formatBrl(price)}/${displayUnit}` : "Sem preço"}
                        </span>
                        <span
                          className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground transition group-hover:scale-110"
                          aria-hidden
                        >
                          <Plus className="h-4 w-4" />
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <aside className="sticky top-4 self-start rounded-xl border bg-card p-4">
          <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <ShoppingCart className="h-4 w-4" />
            Carrinho · {props.cart.length} {props.cart.length === 1 ? "item" : "itens"}
          </div>

          {props.cart.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
              Clique em <strong>+</strong> num produto para começar.
            </div>
          ) : (
            <div className="space-y-2">
              {props.cart.map((it, idx) => {
                const price = priceFor(it.product, it.unit) ?? 0;
                const subtotal = price * it.quantity;
                return (
                  <div key={`${it.productId}-${it.unit}-${it.cut ?? ""}-${idx}`} className="rounded-lg border bg-background p-2">
                    <div className="flex items-start gap-2">
                      <span className="text-lg">{it.product.emoji ?? "📦"}</span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{it.product.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {formatBrl(price)}/{it.unit}
                          {it.cut ? <> · <span className="font-medium text-foreground/80">{it.cut}</span></> : null}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => props.onRemoveItem(idx)}
                        className="text-muted-foreground hover:text-destructive"
                        aria-label="Remover"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => props.onUpdateItem(idx, { quantity: Math.max(0.01, it.quantity - 1) })}
                        className="flex h-7 w-7 items-center justify-center rounded border"
                        aria-label="Diminuir"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={it.quantity}
                        onChange={(e) => props.onUpdateItem(idx, { quantity: Number(e.target.value) })}
                        className="h-7 w-16 text-center"
                      />
                      <button
                        type="button"
                        onClick={() => props.onUpdateItem(idx, { quantity: it.quantity + 1 })}
                        className="flex h-7 w-7 items-center justify-center rounded border"
                        aria-label="Aumentar"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                      <span className="ml-auto text-sm font-semibold">{formatBrl(subtotal)}</span>
                    </div>
                    <Input
                      value={it.observation ?? ""}
                      onChange={(e) => props.onUpdateItem(idx, { observation: e.target.value })}
                      placeholder="Observação do item (opcional)"
                      className="mt-2 h-8 text-xs"
                    />
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-4 space-y-1 border-t pt-3 text-sm">
            <div className="flex justify-between font-bold">
              <span>Total</span>
              <span>{formatBrl(props.total)}</span>
            </div>
          </div>

          <Button
            type="button"
            className="mt-3 w-full"
            onClick={props.onSubmit}
            disabled={!props.canSubmit}
            data-testid="submit-manual-order"
          >
            {props.submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Criar pedido
          </Button>
          <button
            type="button"
            className="mt-1 w-full text-xs text-muted-foreground hover:underline"
            onClick={props.onEditContext}
          >
            <ChevronLeft className="mr-1 inline h-3 w-3" /> Voltar para contexto
          </button>
        </aside>
      </div>
    </>
  );
}

function CategoryPill({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-active={active}
      className="shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition data-[active=true]:border-foreground data-[active=true]:bg-foreground data-[active=true]:text-background"
    >
      {label}
    </button>
  );
}

function AddItemDialog({
  product,
  onClose,
  onConfirm,
}: {
  product: Product | null;
  onClose: () => void;
  onConfirm: (unit: string, qty: number, cut: string | null, observation: string) => void;
}) {
  const [unit, setUnit] = useState<string>("");
  const [qty, setQty] = useState<number>(1);
  // null = "sem corte" (in natura). Outros valores = nome do corte selecionado.
  const [cut, setCut] = useState<string | null>(null);
  const [observation, setObservation] = useState<string>("");

  useEffect(() => {
    if (product) {
      setUnit(displayUnitOf(product));
      setQty(1);
      setCut(null);
      setObservation("");
    }
  }, [product]);

  const units = product ? pricedUnitsOf(product) : [];
  const cuts = product?.availableCuts ?? [];
  const price = product ? priceFor(product, unit) : null;
  const subtotal = (price ?? 0) * qty;

  return (
    <Dialog open={!!product} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="text-2xl">{product?.emoji ?? "📦"}</span>
            {product?.name}
          </DialogTitle>
        </DialogHeader>

        {product ? (
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Unidade</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {units.map((u) => {
                  const p = priceFor(product, u);
                  const selected = unit === u;
                  return (
                    <button
                      key={u}
                      type="button"
                      onClick={() => setUnit(u)}
                      className={`rounded-lg border-2 p-3 text-left text-sm transition ${
                        selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                      }`}
                    >
                      <div className="font-semibold">{u}</div>
                      <div className="text-xs text-muted-foreground">{p != null ? formatBrl(p) : "—"}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {cuts.length > 0 && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tipo de corte</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setCut(null)}
                    className={`rounded-lg border-2 p-2.5 text-left text-sm transition ${
                      cut === null ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                    }`}
                  >
                    Sem corte
                  </button>
                  {cuts.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCut(c)}
                      className={`rounded-lg border-2 p-2.5 text-left text-sm transition ${
                        cut === c ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Quantidade</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.max(0.01, +(q - 1).toFixed(2)))}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border text-lg font-bold"
                  aria-label="Diminuir"
                >
                  −
                </button>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={qty}
                  onChange={(e) => setQty(Number(e.target.value))}
                  className="h-10 text-center text-lg font-semibold"
                />
                <button
                  type="button"
                  onClick={() => setQty((q) => +(q + 1).toFixed(2))}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border text-lg font-bold"
                  aria-label="Aumentar"
                >
                  +
                </button>
              </div>
              {price != null ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  Subtotal: <strong className="text-foreground">{formatBrl(subtotal)}</strong>
                </p>
              ) : (
                <p className="mt-1 text-xs text-amber-600">Produto sem preço para este cliente.</p>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Observação</Label>
              <Textarea
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                placeholder="Observação do item (opcional)"
                rows={2}
                className="mt-1.5 text-sm"
              />
            </div>
          </div>
        ) : null}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            <X className="mr-1 h-4 w-4" /> Cancelar
          </Button>
          <Button
            onClick={() => onConfirm(unit, qty, cut, observation)}
            disabled={!unit || qty <= 0}
          >
            <Plus className="mr-1 h-4 w-4" /> Adicionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function priceFor(product: Product, unit: string): number | null {
  const byUnit = product.priceByUnit?.[unit];
  return typeof byUnit === "number" ? byUnit : null;
}

/**
 * Unidade destacada no card/pré-seleção: a unidade padrão do produto quando ela tem preço para este
 * cliente; senão a primeira unidade com preço. Assim, quando a tabela do cliente/unidade só cadastra o
 * produto numa unidade diferente da padrão, o card mostra a unidade e o preço que a tabela realmente tem
 * (em vez de herdar a unidade padrão da Tabela Padrão do segmento).
 */
function displayUnitOf(product: Product): string {
  const priced = Object.keys(product.priceByUnit ?? {});
  if (product.priceByUnit?.[product.defaultUnit] != null) return product.defaultUnit;
  return priced[0] ?? product.defaultUnit;
}

/** Unidades ofertáveis no pedido: as que têm preço para este cliente (fallback = unidade de exibição). */
function pricedUnitsOf(product: Product): string[] {
  const priced = (product.availableUnits ?? []).filter((u) => priceFor(product, u) != null);
  return priced.length > 0 ? priced : [displayUnitOf(product)];
}

function formatBrl(value: number): string {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
